import React, { useState } from 'react';
import { Box, Typography, Tooltip, IconButton, Paper, useTheme } from '@mui/material';
import { CheckCircle as StatusIcon, Error as HealthIcon, Warning as AlertIcon } from '@mui/icons-material';

interface RaspiStatusIndicatorProps {
  status: string;
  statusDescription?: string;
  health: string;
  healthDescription?: string;
  alert: string;
  alertDescription?: string;
}

const getStatusIcon = (status: string, iconSize: string) => {
  switch (status?.toUpperCase()) {
    case 'AVAILABLE':
      return <StatusIcon sx={{ color: '#4caf50', fontSize: iconSize }} />;
    case 'IN_USE':
      return <StatusIcon sx={{ color: '#2196f3', fontSize: iconSize }} />;
    case 'DISCONNECTED':
      return <StatusIcon sx={{ color: '#9e9e9e', fontSize: iconSize }} />;
    default:
      return <StatusIcon sx={{ color: '#9e9e9e', fontSize: iconSize }} />;
  }
};
const getStatusColor = (status: string) => {
  switch (status?.toUpperCase()) {
    case 'AVAILABLE':
      return '#4caf50';
    case 'IN_USE':
      return '#2196f3';
    case 'DISCONNECTED':
      return '#9e9e9e';
    default:
      return '#9e9e9e';
  }
};
const getHealthIcon = (health: string, iconSize: string) => {
  switch (health?.toUpperCase()) {
    case 'OK':
      return <StatusIcon sx={{ color: '#4caf50', fontSize: iconSize }} />;
    case 'CHECK':
      return <HealthIcon sx={{ color: '#ff9800', fontSize: iconSize }} />;
    default:
      return <StatusIcon sx={{ color: '#9e9e9e', fontSize: iconSize }} />;
  }
};
const getHealthColor = (health: string) => {
  switch (health?.toUpperCase()) {
    case 'OK':
      return '#4caf50';
    case 'CHECK':
      return '#ff9800';
    case 'ERROR':
      return '#f44336';
    case 'UNKNOWN':
      return '#757575';
    default:
      return '#4caf50';
  }
};
const getAlertIcon = (alert: string, iconSize: string) => {
  switch (alert?.toUpperCase()) {
    case 'CRITICAL':
    case 'HIGH':
      return <AlertIcon sx={{ color: '#f44336', fontSize: iconSize }} />;
    case 'MEDIUM':
    case 'LOW':
      return <AlertIcon sx={{ color: '#ff9800', fontSize: iconSize }} />;
    case 'NO':
    case 'NONE':
      return <AlertIcon sx={{ color: '#4caf50', fontSize: iconSize }} />;
    default:
      return <AlertIcon sx={{ color: '#9e9e9e', fontSize: iconSize }} />;
  }
};
const getAlertColor = (alert: string) => {
  switch (alert?.toUpperCase()) {
    case 'HIGH':
      return '#f44336';
    case 'LOW':
      return '#ff9800';
    case 'NO':
      return '#4caf50';
    default:
      return '#9e9e9e';
  }
};

export const RaspiStatusIndicator: React.FC<RaspiStatusIndicatorProps> = ({
  status,
  statusDescription,
  health,
  healthDescription,
  alert,
  alertDescription,
}) => {
  const theme = useTheme();
  // Individual state for each indicator
  const [showStatusDescription, setShowStatusDescription] = useState<boolean>(false);
  const [showHealthDescription, setShowHealthDescription] = useState<boolean>(false);
  const [showAlertDescription, setShowAlertDescription] = useState<boolean>(false);

  // Individual click handlers
  const handleStatusClick = () => {
    setShowStatusDescription(!showStatusDescription);
    // Close other descriptions
    setShowHealthDescription(false);
    setShowAlertDescription(false);
  };

  const handleHealthClick = () => {
    setShowHealthDescription(!showHealthDescription);
    // Close other descriptions
    setShowStatusDescription(false);
    setShowAlertDescription(false);
  };

  const handleAlertClick = () => {
    setShowAlertDescription(!showAlertDescription);
    // Close other descriptions
    setShowStatusDescription(false);
    setShowHealthDescription(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
        {/* Status Indicator */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, p: 0.2, minWidth: 50 }}>
          <Tooltip title={statusDescription || ''} arrow>
            <IconButton
              onClick={handleStatusClick}
              size="small"
              sx={{ color: getStatusColor(status), background: 'transparent' }}
            >
              {getStatusIcon(status, theme.iconSizes.small)}
            </IconButton>
          </Tooltip>
          <Typography variant="iconValueText" sx={{ color: getStatusColor(status) }}>{status}</Typography>
          <Typography variant="iconLabelText" sx={{ color: 'text.secondary' }}>STATUS</Typography>
        </Box>

        {/* Health Indicator */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, p: 0.2, minWidth: 50 }}>
          <Tooltip title={healthDescription || ''} arrow>
            <IconButton
              onClick={handleHealthClick}
              size="small"
              sx={{ color: getHealthColor(health), background: 'transparent' }}
            >
              {getHealthIcon(health, theme.iconSizes.small)}
            </IconButton>
          </Tooltip>
          <Typography variant="iconValueText" sx={{ color: getHealthColor(health) }}>{health}</Typography>
          <Typography variant="iconLabelText" sx={{ color: 'text.secondary' }}>HEALTH</Typography>
        </Box>

        {/* Alert Indicator */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, p: 0.2, minWidth: 50 }}>
          <Tooltip title={alertDescription || ''} arrow>
            <IconButton
              onClick={handleAlertClick}
              size="small"
              sx={{ color: getAlertColor(alert), background: 'transparent' }}
            >
              {getAlertIcon(alert, theme.iconSizes.small)}
            </IconButton>
          </Tooltip>
          <Typography variant="iconValueText" sx={{ color: getAlertColor(alert) }}>{alert}</Typography>
          <Typography variant="iconLabelText" sx={{ color: 'text.secondary' }}>ALERT</Typography>
        </Box>
      </Box>

      {/* Individual Description Panels */}
      {showStatusDescription && statusDescription && (
        <Paper elevation={2} sx={{ mt: 1, p: 1.5, minWidth: 220, maxWidth: 320, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {statusDescription}
          </Typography>
        </Paper>
      )}

      {showHealthDescription && healthDescription && (
        <Paper elevation={2} sx={{ mt: 1, p: 1.5, minWidth: 220, maxWidth: 320, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {healthDescription}
          </Typography>
        </Paper>
      )}

      {showAlertDescription && alertDescription && (
        <Paper elevation={2} sx={{ mt: 1, p: 1.5, minWidth: 220, maxWidth: 320, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {alertDescription}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default RaspiStatusIndicator; 